import ReportList from 'components/Navigation/ReportList'
import ScreenLoading from 'components/ScreenLoading'
import Feature from 'components/Feature'
import useListAccountsWithWarnings from 'containers/services/useGetAccountsWithWarnings'

import React, { useContext, useEffect, useRef, useState } from 'react'
import { PopupButton } from 'react-calendly'
import TagManager from 'react-gtm-module'

import { useHistory } from 'react-router-dom'
import { useSidebarCtx } from 'containers/SidebarContext'
import { useReportsCtx } from 'containers/reports/ReportsContext'
import Divider from 'components/Divider'

import mayaLogo from "images/maya-logo.png"
import useBookmarks from 'containers/embed/useBookmarks'
import { BookmarksContext } from 'containers/bookmarks/BookmarksContext'

const SideBar = ({
  embedReports,
  embedReportsFetchStatus,
  organizationSlug,
  page,
  currentOrganization,
  user,
  embedReportSlug,
  embedViewMode,
  subscription,
}) => {
  const refIsMounted = useRef(false)
  const history = useHistory()
  const [setupCollapsed, setSetupCollapsed] = useState(true)
  const [isActiveReportExpanded, setIsActiveReportExpanded] = useState(true)
  const [isReportsExpanded, setIsReportsExpanded] = useState(false)
  const [isCustomReportsExpanded, setIsCustomReportsExpanded] = useState(false)
  const [isDemoExpanded, setIsDemoExpanded] = useState(false)
  const [sourceWarnings, fetchSourceWarnings] = useListAccountsWithWarnings()
  const {fetchingBookmark} = useBookmarks(currentOrganization.id)
  const {navigationBookmarks} = useContext(BookmarksContext)
  const { isSidebarCollapsed, isEmbedEnabled, collapseSidebar } =
    useSidebarCtx()
  const { embedReport, reportPages, report, setReport, clearReport } =
    useReportsCtx()
  const [activeDemoReport, setActiveDemoReport] = useState('')

  useEffect(() => {
    if (history.location.pathname.includes("/demo/Demo") || history.location.pathname.includes("-demo")) {
      setActiveDemoReport('Demo')
      setIsDemoExpanded(true)
      setIsReportsExpanded(false)
    } else if (history.location.pathname.includes("/dashboard/")) {
      setIsReportsExpanded(true)
      setIsDemoExpanded(false)
      setSetupCollapsed(true)
    }else {
      setActiveDemoReport('')
    }
  }, [history.location.pathname])

  const handleReportClick = (reportName, embedReportSlug) => {
    setReport(() => ({ name: reportName, slug: embedReportSlug }))
    setIsActiveReportExpanded(true)
    setIsReportsExpanded(false)
    setIsCustomReportsExpanded(false)
    setSetupCollapsed(true)
    if (window.innerWidth < 767) {
      collapseSidebar(true)
    }
  }

  useEffect(() => {
    clearReport()
    fetchSourceWarnings(currentOrganization.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization.id])

  useEffect(() => {
    refIsMounted.current = true
    return () => {
      refIsMounted.current = false
    }
  }, [collapseSidebar])

  const redirectTo = (url) => {
    history.push(url)
  }

  const handleRedirectToTab = (tabName) => {
    TagManager.dataLayer({ dataLayer: { event: `clicked_sidebar_tab_${tabName}`, is_custom_track: true } })
    if (tabName === 'billingDetails') {
      if (subscription) {
        redirectTo(`/organization/${organizationSlug}/billing/details`)
      } else {
        redirectTo(
          `/organization/${organizationSlug}/billing/details?checkout=1`
        )
      }
    } else {
      redirectTo(`/organization/${organizationSlug}/${tabName}`)
    }
    if (window.innerWidth < 550) {
      collapseSidebar(true)
    }
  }

  if (isSidebarCollapsed === null) {
    return null
  }

  const mayaReports =
    embedReportsFetchStatus !== 'pending'
      ? embedReports.filter((r) => !r.is_custom)
      : []
  const customReports =
    embedReportsFetchStatus !== 'pending'
      ? embedReports.filter((r) => r.is_custom)
      : []

  const handleOpenSection = (section) => {
    switch (section) {
      case 'setup':
        setSetupCollapsed(prev => !prev)
        setIsReportsExpanded(false)
        setIsCustomReportsExpanded(false)
        setIsActiveReportExpanded(false)
        setIsDemoExpanded(false)
        break
      case 'reports':
        setSetupCollapsed(true)
        setIsReportsExpanded((prev) => !prev)
        setIsCustomReportsExpanded(false)
        setIsActiveReportExpanded(false)
        setIsDemoExpanded(false)
        break
      case 'demo':
        setSetupCollapsed(true)
        setIsReportsExpanded(false)
        setIsCustomReportsExpanded(false)
        setIsActiveReportExpanded(false)
        setIsDemoExpanded((prev) => !prev)
        break
      case 'customReports':
        setSetupCollapsed(true)
        setIsReportsExpanded(false)
        setIsCustomReportsExpanded((prev) => !prev)
        setIsActiveReportExpanded(false)
        setIsDemoExpanded(false)
        break
      case 'activeReport':
        setSetupCollapsed(true)
        setIsReportsExpanded(false)
        setIsCustomReportsExpanded(false)
        setIsActiveReportExpanded((prev) => !prev)
        setIsDemoExpanded(false)
        break
      case 'marketplace':
        setSetupCollapsed(true)
        setIsReportsExpanded(false)
        setIsCustomReportsExpanded(false)
        setIsActiveReportExpanded(false)
        setIsDemoExpanded(false)
        handleRedirectToTab('billingDetails')
        break
      default:
        break
    }
  }

   const handleOpenDemoReport = (reportName) => {
    setActiveDemoReport(reportName)
    history.push(`/organization/${organizationSlug}/dashboard/-demo`)

    if (window.innerWidth < 767) {
      collapseSidebar(true)
    }
   }

  const handleToggleActiveReport = (v) => {
    setIsActiveReportExpanded(v)
    setIsCustomReportsExpanded(false)
    setIsReportsExpanded(false)
    setSetupCollapsed(true)
  }

  const handleClickLogo = () => {
    history.push('/')
  }

  const CollapsedSidebar = ({
    currentOrganization,
    hasCustomReports,
    hasActiveReport,
    handleOpenSection,
    isActiveReportExpanded,
    isCustomReportsExpanded,
    isReportsExpanded,
    setupCollapsed,
  }) => {
    const handleClick = (section) => {
      handleOpenSection(section)
      collapseSidebar(false)
    }

    return (
      <div className="collapsed-sidebar">
        {!currentOrganization.is_demo && (
          <div
            className="icon-container"
            onClick={() =>
              setupCollapsed ? handleClick('setup') : collapseSidebar(false)
            }
          >
            <span className="material-icons-outlined non-selectable">tune</span>
          </div>
        )}
        <Feature feature="demo" switchComponent={<></>}>
          <div
            className="icon-container"
            onClick={() =>
              !isDemoExpanded ? handleClick('demo') : collapseSidebar(false)
            }
          >
            <span className="material-icons-outlined non-selectable">
              data_usage
            </span>
          </div>
        </Feature>
        {isEmbedEnabled && (
          <Feature feature="reports" switchComponent={<></>}>
            <div
              className="icon-container"
              onClick={() =>
                !isReportsExpanded ? handleClick('reports') : collapseSidebar(false)
              }
            >
              <span className="material-icons-outlined non-selectable">
                dashboard
              </span>
            </div>
          </Feature>
        )}
        {hasCustomReports && (
          <Feature feature="custom_reports" switchComponent={<></>}>
            <div
              className="icon-container"
              onClick={() =>
                !isCustomReportsExpanded
                  ? handleClick('customReports')
                  : collapseSidebar(false)
              }
            >
              <span className="material-icons-outlined non-selectable">
                dashboard_customize
              </span>
            </div>
          </Feature>
        )}
      </div>
    )
  }


  return (
    <div className={`sidebar ${isSidebarCollapsed ? 'collapsed' : 'open'}`}>
      <div
        onClick={handleClickLogo}
        className="logo"
        style={{ display: 'none' }}
      >
        <img src={mayaLogo} alt="Maya" />
      </div>
      {isSidebarCollapsed ? (
        <CollapsedSidebar
          currentOrganization={currentOrganization}
          hasCustomReports={customReports.length > 0}
          hasActiveReport={embedReport}
          handleOpenSection={(section) => handleOpenSection(section)}
          isActiveReportExpanded={isActiveReportExpanded}
          isCustomReportsExpanded={isCustomReportsExpanded}
          isReportsExpanded={isReportsExpanded}
          setupCollapsed={setupCollapsed}
        />
      ) : (
        <>
          <ul className="navigation">
            {!currentOrganization.is_demo && (
              <>
                <li
                  className={`main-navigation setup-${
                    setupCollapsed ? 'collapsed' : 'expanded'
                  }`}
                  onClick={() => handleOpenSection('setup')}
                >
                  <div className="setup-header">
                    <div>
                      <span className="material-icons-outlined non-selectable">
                        tune
                      </span>
                      Setup
                    </div>
                    <div>
                      {setupCollapsed &&
                        sourceWarnings &&
                        sourceWarnings.length > 0 && (
                          <span className="material-icons-outlined warning-icon">
                            warning
                          </span>
                        )}
                      <span
                        className={`material-icons-outlined ${
                          !setupCollapsed && 'rotate'
                        }`}
                      >
                        keyboard_arrow_right
                      </span>
                    </div>
                  </div>
                  <ul className="sub-navigation">
                    <li
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRedirectToTab('settings')
                      }}
                      className={page === 'settings' ? 'active' : ''}
                    >
                      <span className="material-icons-outlined">
                        manage_accounts
                      </span>
                      Administration
                    </li>
                    {/* Data & Insights merges the former Sources, ETL and Maya MCP items
                        into one guided flow (Integrations › Data Sync › Ask Maya). Stays
                        highlighted on the flow itself, its source-setup sub-pages and the
                        Manual Expenses pages (which live under Sources) — the keys below
                        must match the renderPage() page keys of the /sources/* and
                        /expenses routes in OrganizationRoute.jsx; update both together
                        when adding a source-setup page. */}
                    <li
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRedirectToTab('data-insights')
                      }}
                      className={
                        [
                          'data-insights',
                          'add-source',
                          'redirect-source',
                          'custom-table',
                          'google-play',
                          'moosend',
                          'klaviyo',
                          'shopify',
                          'apple-ads',
                          'apple-app-store',
                          'google_analytics_4_events',
                          'expenses',
                        ].includes(page)
                          ? 'active'
                          : ''
                      }
                    >
                      <span className="material-icons-outlined">insights</span>
                      Data &amp; Insights
                      {sourceWarnings.length > 0 && (
                        <span className="material-icons-outlined warning-icon">
                          warning
                        </span>
                      )}
                    </li>
                    <Feature feature="clusters" switchComponent={<></>}>
                      <li
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRedirectToTab('clusters')
                        }}
                        className={page === 'clusters' ? 'active' : ''}
                      >
                        <span className="material-icons-outlined">code</span>
                        Clusters
                      </li>
                    </Feature>
                    <Feature feature="competition" switchComponent={<></>}>
                      <li
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRedirectToTab('competitors')
                        }}
                        className={page === 'competitors' ? 'active' : ''}
                      >
                        <span className="material-icons-outlined">
                          trending_up
                        </span>
                        Competitors
                      </li>
                    </Feature>
                    <Feature feature="pagespeed" switchComponent={<></>}>
                      <li
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRedirectToTab('pagespeed')
                        }}
                        className={page === 'pagespeed' ? 'active' : ''}
                      >
                        <span className="material-icons-outlined">speed</span>
                        PageSpeed
                      </li>
                    </Feature>
                    <Feature feature="marketplace" switchComponent={<></>}>
                      <li
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRedirectToTab('billingDetails')
                        }}
                        className={`${page === 'billingDetails' || page === 'billingProducts' || page === 'billingCheckout' ? 'active' : ''}`}
                      >
                        <div>
                          <span className="material-icons-outlined">
                            storefront
                          </span>
                          Marketplace
                        </div>
                      </li>
                    </Feature>
                  </ul>
                </li>
                <Feature feature="demo" switchComponent={<></>}>
                  <li
                    className={`main-navigation demo-${
                      isDemoExpanded ? 'expanded' : 'collapsed'
                    }`}
                    onClick={() => handleOpenSection('demo')}
                  >
                    <div className="demo-header">
                      <div>
                        <span className="material-icons-outlined non-selectable">
                          data_usage
                        </span>
                        Demo
                      </div>
                      <span
                        className={`material-icons-outlined ${
                          isDemoExpanded && 'rotate'
                        }`}
                      >
                        keyboard_arrow_right
                      </span>
                    </div>
                    <ul className="sub-navigation">
                      <li
                        className={
                          activeDemoReport === 'Demo' ? 'active' : ''
                        }
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleOpenDemoReport('Demo')
                        }}
                      >
                        Performance
                      </li>
                    </ul>
                    <PopupButton
                      url="https://calendly.com/mayainsights/book-a-demo"
                      text="Book a Demo"
                      onClick={() => {
                        TagManager.dataLayer({
                          dataLayer: {
                            event: `demo_report_visited`,
                            is_custom_track: true,
                          },
                        })
                      }}
                      className="submit-button"
                      styles={{
                        left: 0,
                        margin: '5px 10px 0 10px',
                        width: '80%',
                        aligSelf: 'center',
                      }}
                      prefill={{
                        email: user ? user.email : '',
                        firstName:
                          user && user.profile ? user.profile.first_name : '',
                        lastName:
                          user && user.profile ? user.profile.last_name : '',
                        name:
                          user && user.profile
                            ? user.profile.first_name +
                              ' ' +
                              user.profile.last_name
                            : '',
                      }}
                    />
                  </li>
                </Feature>
                {isEmbedEnabled && (
                  <>
                    <Feature feature="reports" switchComponent={<></>}>
                      <li
                        className={`main-navigation reports-${
                          isReportsExpanded ? 'expanded' : 'collapsed'
                        }`}
                        onClick={() => handleOpenSection('reports')}
                      >
                        <div className="reports-header">
                          <div>
                            <span className="material-icons-outlined non-selectable">
                              dashboard
                            </span>
                            {currentOrganization.name} Reports
                          </div>
                          <span
                            className={`material-icons-outlined ${
                              isReportsExpanded && 'rotate'
                            }`}
                          >
                            keyboard_arrow_right
                          </span>
                        </div>
                        <ReportList
                          embedReportsFetchStatus={embedReportsFetchStatus}
                          embedReports={mayaReports}
                          currentOrganization={currentOrganization}
                          currentReportSlug={embedReportSlug}
                          onReportOpen={(reportName) =>
                            handleReportClick(reportName, embedReportSlug)
                          }
                          navigationBookmarks={navigationBookmarks}
                          fetchingBookmark={fetchingBookmark}
                        />
                      </li>
                    </Feature>
                    {embedReportsFetchStatus !== 'pending' &&
                      customReports.length > 0 && (
                        <Feature feature="custom_reports" switchComponent={<></>}>
                          <li
                            className={`main-navigation custom-reports-${
                              isCustomReportsExpanded
                                ? 'expanded'
                                : 'collapsed'
                            }`}
                            onClick={() => handleOpenSection('customReports')}
                          >
                            <div className="custom-reports-header">
                              <div>
                                <span className="material-icons-outlined">
                                  dashboard_customize
                                </span>
                                Custom {currentOrganization.name} Reports
                              </div>
                              <span
                                className={`material-icons-outlined ${
                                  isCustomReportsExpanded && 'rotate'
                                }`}
                              >
                                keyboard_arrow_right
                              </span>
                            </div>
                            <ReportList
                              embedReportsFetchStatus={
                                embedReportsFetchStatus
                              }
                              embedReports={customReports}
                              currentOrganization={currentOrganization}
                              currentReportSlug={embedReportSlug}
                              onReportOpen={(reportName) =>
                                handleReportClick(reportName, embedReportSlug)
                              }
                              navigationBookmarks={navigationBookmarks}
                              fetchingBookmark={fetchingBookmark}
                            />
                          </li>
                        </Feature>
                      )}
                  </>
                )}
              </>
            )}
          </ul>
        </>
      )}
    </div>
  )
}

export default SideBar
